<template>
  <v-container>
    <v-row>
      <v-col>
        <h1>Dashboard</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-switch
          v-model="includeContingentWorkers"
          inset
          label="Include Contingent Workers"
        ></v-switch>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-autocomplete
          v-model="departmentFilter"
          :items="departmentFilterOptions"
          label="Departments"
          solo
          rounded
          multiple
          chips
        ></v-autocomplete>
      </v-col>
      <v-col>
        <v-autocomplete
          v-model="locationFilter"
          :items="locationFilterOptions"
          label="Locations"
          solo
          rounded
          multiple
          chips
        ></v-autocomplete>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="6">
        <PieCard
          title="Manager Training"
          :pieData="managerTrainingPieChartData"
          :panels="[
            {
              employees: needsManagerTraining,
              header: 'Needs Manager Training'
            },
            {
              employees: attendedManagerTraining,
              header: 'Attended Manager Training'
            }
          ]"
        />
      </v-col>
      <v-col cols="6">
        <PieCard
          title="Sexual Harassment Training"
          :pieData="sexualHarassmentTrainingPieChartData"
          :panels="[
            {
              employees: sexualHarassmentTrainingOverdue,
              header: 'Needs Sexual Harassment Training'
            },
            {
              employees: sexualHarassmentTrainingCompliant,
              header: 'Attended Sexual Harassment Training'
            },
            {
              employees: sexualHarassmentTrainingOverdueNextMonth,
              header: 'Will Require Sexual Harassment Training Next Month'
            }
          ]"
        />
      </v-col>
      <v-col cols="6">
        <PieCard
          title="Diversity Training"
          :pieData="diversityTrainingPieChartData"
          :panels="[
            {
              employees: needsDiversityTraining,
              header: 'Needs Diversity Training'
            },
            {
              employees: attendedDiversityTraining,
              header: 'Attended Diversity Training'
            }
          ]"
        />
      </v-col>
      <v-col cols="6">
        <PieCard
          title="COVID-19 Training"
          :pieData="covid19TrainingPieChartData"
          :panels="[
            {
              employees: needsCovid19Training,
              header: 'Needs COVID-19 Training'
            },
            {
              employees: attendedCovid19Training,
              header: 'Attended COVID-19 Training'
            }
          ]"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import moment from 'moment'
  import PieCard from '@/components/dashboard/PieCard'
  
  export default {
    name: 'Dashboard',
    components: {
      PieCard
    },
    data () {
      return {
        departmentFilter: [],
        locationFilter: [],
        includeContingentWorkers: false
      }
    },
    created () {
      this.$store.dispatch('getRecords', {
        table: 'Employees',
        view: 'All Employees',
        dispatch: 'setEmployees'
      })
    },
    computed: {
      departmentFilterOptions () {
        return this.$store.getters.getEmployees.filter(e => {
          return e.fields.Department
        }).map(e => {
          return e.fields.Department
        }).sort((a, b) => {
          return a > b ? 1 : -1
        })
      },
      locationFilterOptions () {
        return this.$store.getters.getEmployees.filter(e => {
          return e.fields.Location
        }).map(e => {
          return e.fields.Location
        }).sort((a, b) => {
          return a > b ? 1 : -1
        })
      },
      employees () {
        let employees = this.$store.getters.getEmployees.filter(e => {
          return e.fields.Status === 'Active'
        })
        if (!this.includeContingentWorkers) {
          employees = employees.filter(e => {
            return e.fields['Is In ADP']
          })
        }
        if (this.departmentFilter.length > 0) {
          employees = employees.filter(e => {
            return this.departmentFilter.includes(e.fields.Department)
          })
        }
        if (this.locationFilter.length > 0) {
          employees = employees.filter(e => {
            return this.locationFilter.includes(e.fields.Location)
          })
        }
        return employees
      },
      sexualHarassmentHeaders () {
        return [{
          text: 'Latest Sexual Harassment Training',
          align: 'start',
          sortable: true,
          value: 'fields.Latest Sexual Harassment Training'
        }]
      },
      sexualHarassmentTrainingOverdue () {
        return this.employees.filter(e => {
          const daysInYear = 365
          let latestTraining
          if (e.fields['Latest Sexual Harassment Training']) {
            latestTraining = e.fields['Latest Sexual Harassment Training']
          } else {
            latestTraining = null
          }
          if (latestTraining === null) {
            return true
          } else if (this.dayDiff(daysInYear, latestTraining)) {
            return true
          }
        })
      },
      sexualHarassmentTrainingOverdueNextMonth () {
        return this.employees.filter(e => {
          let latestTraining
          if (e.fields['Latest Sexual Harassment Training']) {
            latestTraining = e.fields['Latest Sexual Harassment Training']
          } else {
            latestTraining = null
          }
          if (latestTraining === null) {
            return false
          } else if (this.needsTrainingNextMonth(latestTraining)) {
            return true
          }
        })
      },
      sexualHarassmentTrainingCompliant () {
        return this.employees.filter(e => {
          return !this.sexualHarassmentTrainingOverdue.includes(e)
        })
      },
      sexualHarassmentTrainingPieChartData () {
        return {
          series: [
            this.sexualHarassmentTrainingOverdue.length,
            this.sexualHarassmentTrainingCompliant.length
          ],
          labels: [
            'Needs Sexual Harassment Training',
            'Attended Sexual Harassment Training'
          ]
        }
      },
      managers () {
        const managerEmailsArray = this.employees.map(e => {
          return e.fields['Manager Email']
        })
        const managerEmailSet = new Set(managerEmailsArray)
        return this.employees.filter(e => {
          return managerEmailSet.has(e.fields.Email)
        })
      },
      needsManagerTraining () {
        return this.managers.filter(m => {
          return !m.fields['Latest Manager Training']
        })
      },
      attendedManagerTraining () {
        return this.managers.filter(m => {
          return m.fields['Latest Manager Training']
        })
      },
      managerTrainingPieChartData () {
        return {
          series: [
            this.needsManagerTraining.length,
            this.managers.length - this.needsManagerTraining.length
          ],
          labels: ['Needs Manager Training', 'Attended Manager Training']
        }
      },
      attendedDiversityTraining () {
        return this.employees.filter(e => {
          return e.fields['Latest Diversity Training']
        })
      },
      needsDiversityTraining () {
        return this.employees.filter(e => {
          return !this.attendedDiversityTraining.includes(e)
        })
      },
      diversityTrainingPieChartData () {
        return {
          series: [
            this.needsDiversityTraining.length,
            this.attendedDiversityTraining.length
          ],
          labels: [
            'Needs Diversity Training',
            'Attended Diversity Training'
          ]
        }
      },
      attendedCovid19Training () {
        return this.employees.filter(e => {
          return e.fields['Latest COVID-19 Training']
        })
      },
      needsCovid19Training () {
        return this.employees.filter(e => {
          return e.fields['Location Formatted'] !== 'Remote' &&
                 !this.attendedCovid19Training.includes(e)
        })
      },
      covid19TrainingPieChartData () {
        return {
          series: [
            this.needsCovid19Training.length,
            this.attendedCovid19Training.length
          ],
          labels: [
            'Needs COVID-19 Training',
            'Attended COVID-19 Training'
          ]
        }
      }
    },
    methods: {
      dayDiff (numDays, date) {
        return moment(moment()).diff(date, 'days') >= numDays
      },
      needsTrainingNextMonth (latestTraining) {
        const nextMonth = moment().add(1, 'months').month()
        const lastTrainingMonth = moment(latestTraining).month()
        return nextMonth === lastTrainingMonth
      }
    }
  }
</script>
